<template>
  <div class="onboarding-step registration-form">
    <div class="step-header">
      <h3>{{ t('Informações do Usuário') }}</h3>
      <p class="step-description">{{ t('Preencha os dados básicos para criar a conta do usuário') }}</p>
    </div>

    <inline-message v-if="errorMessage" severity="error" :closable="true" @close="errorMessage = null">
      {{ errorMessage }}
    </inline-message>

    <div class="form-grid">
      <!-- Username -->
      <input-text
        :model-value="localData.username"
        @update:model-value="setField('username', $event)"
        elem-id="username"
        :label="t('Nome de usuário')"
        :placeholder="t('Nome de usuário')"
        :required="!editMode"
        :validation="() => !errors.username"
        :disabled="editMode"
        data-testid="users-onboarding-username"
        @blur="validateField('username')"
      />

      <!-- Name -->
      <input-text
        :model-value="localData.name"
        @update:model-value="setField('name', $event)"
        elem-id="onboarding_name"
        :label="t('Nome completo')"
        :placeholder="t('Nome completo')"
        :required="true"
        :validation="() => !errors.name"
        data-testid="users-onboarding-name"
        @blur="validateField('name')"
      />

      <!-- Email -->
      <input-text
        :model-value="localData.email"
        @update:model-value="setField('email', $event)"
        elem-id="onboarding_email"
        type="email"
        :label="t('Email')"
        :placeholder="t('Email')"
        :required="true"
        :validation="() => !errors.email"
        data-testid="users-onboarding-email"
        @blur="validateField('email')"
      />

      <!-- Phone -->
      <input-phone
        v-model="phoneModel"
        elem-id="phone_number"
        :label="t('Telefone')"
        :placeholder="t('Telefone')"
        :ng-required="true"
        :language="currentLanguage"
        data-testid="users-onboarding-phone"
        @blur="validateField('phone_number')"
      />

      <!-- Documento: CPF, ou passaporte/RNE para estrangeiro (NS 2026-0083) -->
      <input-text
        :model-value="localData.documento"
        @update:model-value="setField('documento', $event)"
        elem-id="onboarding_documento"
        :label="t('Documento (CPF, passaporte ou RNE)')"
        :required="true"
        :validation="() => !errors.documento"
        data-testid="users-onboarding-documento"
        @blur="validateField('documento')"
      />

      <!-- Password (only in create mode, edit uses ChangePasswordModal) -->
      <div v-if="!editMode" class="form-field">
        <label for="password" class="required">{{ t('Senha') }}</label>
        <input-password
          elem-id="password"
          :model-value="localData.password"
          @update:model-value="setField('password', $event)"
          :toggle-mask="true"
          :feedback="false"
          autocomplete="new-password"
          data-testid="users-onboarding-password"
          @blur="validateField('password')"
        />
        <small v-if="errors.password" class="p-error">{{ errors.password }}</small>
        <small v-else class="p-help">{{ t('Mínimo 8 caracteres') }}</small>
      </div>

      <!-- Confirm Password (only in create mode) -->
      <div v-if="!editMode" class="form-field">
        <label for="confirmPassword" class="required">{{ t('Confirmar Senha') }}</label>
        <input-password
          elem-id="confirmPassword"
          :model-value="localData.confirmPassword"
          @update:model-value="setField('confirmPassword', $event)"
          :toggle-mask="true"
          :feedback="false"
          autocomplete="new-password"
          data-testid="users-onboarding-password-confirm"
          @blur="validateField('confirmPassword')"
        />
        <small v-if="errors.confirmPassword" class="p-error">{{ errors.confirmPassword }}</small>
      </div>

      <!-- Language -->
      <select-list
        :model-value="localData.language"
        @update:model-value="setField('language', $event)"
        elem-id="onboarding_language"
        :label="t('Idioma')"
        :itens-select="languageOptions"
        data-testid="users-onboarding-language"
      />

      <!-- MCP access (custom:mcp_scopes) is edited in the Permissions step via
           the tools picker, never as free text — formData.mcp_scopes only travels here. -->

      <!-- Versão do Sistema -->
      <select-list
        :model-value="localData.versaoSistema"
        @update:model-value="setField('versaoSistema', $event)"
        elem-id="onboarding_versaoSistema"
        :label="t('Versão do Sistema')"
        :itens-select="versaoSistemaOptions"
        data-testid="users-onboarding-versao-sistema"
      />

      <!-- Background Color -->
      <color-picker
        :model-value="localData.bgcolor"
        @update:model-value="setField('bgcolor', $event)"
        elem-id="onboarding_bgcolor"
        :label="t('Cor de Fundo')"
        data-testid="users-onboarding-bgcolor"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';
import { phoneFromString, phoneToString } from '@/composables/usePhoneFormat';
import { useLanguages } from '@/composables/useLanguages';

interface FormData {
  username: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone_number: string;
  documento: string;
  language: string;
  versaoSistema: string;
  bgcolor: string;
  mcp_scopes: string;
  [key: string]: string;
}

interface FormErrors {
  [field: string]: string | undefined;
}

interface LanguageOption {
  ID: string;
  Name: string;
}

interface ValidationRule {
  required: boolean;
  minLength?: number;
  pattern?: RegExp;
  custom?: () => boolean;
  message?: string;
}

const { t, locale } = useI18n();

// Props
const props = withDefaults(defineProps<{
  modelValue: FormData;
  errors?: FormErrors;
  editMode?: boolean;
}>(), {
  errors: () => ({}),
  editMode: false
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: FormData];
  'validate': [isValid: boolean, errors: FormErrors];
}>();

// Local state
/*
 * Read-only view of the parent's model. Every change goes UP as a new object
 * through setField() — never written into the prop. Writing in place
 * (`localData.value.x = …`) changed the parent's object behind its back: when
 * InputPhone's mask cut a 16-digit number down to a valid one, the step
 * reported "valid" for a number the parent never chose (users.integration
 * "accepts international E.164 phones…", NS 2026-0083).
 */
const localData = computed<FormData>(() => props.modelValue);

/** The text inputs emit `undefined` when cleared; the model keeps '' (what it started with). */
const setField = <K extends keyof FormData>(field: K, value: FormData[K] | undefined): void => {
  emit('update:modelValue', { ...props.modelValue, [field]: value ?? '' });
};

const errors = ref<FormErrors>({});
const errorMessage = ref<string | null>(null);

// Current language for phone component
const currentLanguage = computed<string>(() => locale.value || 'pt-BR');

// Phone model conversion
const phoneModel = computed({
  get: () => phoneFromString(localData.value.phone_number || ''),
  set: (value) => {
    setField('phone_number', phoneToString(value));
  }
});

const { fetchLanguages } = useLanguages();
const languageOptions = ref<LanguageOption[]>([
  { ID: 'pt-BR', Name: 'Português (BR)' },
  { ID: 'en-US', Name: 'English (US)' },
  { ID: 'es-ES', Name: 'Español (ES)' }
]);

onMounted(async () => {
  try {
    const langs = await fetchLanguages() as unknown as LanguageOption[];
    if (langs.length > 0) {
      languageOptions.value = langs;
    }
  } catch (err) {
    log('OnboardingStep0Registration', 'onMounted', 'Failed to load languages, using defaults', err);
  }
});

// Versão Sistema options - matching UserForm
const versaoSistemaOptions: LanguageOption[] = [
  { ID: '0', Name: 'Versão 0 (Padrão)' },
  { ID: '1', Name: 'Versão 1 (Avançada)' }
];

// Validation rules (computed to handle edit mode)
const validationRules = computed<Record<string, ValidationRule>>(() => ({
  username: {
    required: !props.editMode,
    minLength: 3,
    pattern: /^[a-zA-Z0-9_]+$/,
    message: t('Usuário deve ter no mínimo 3 caracteres (letras, números e underscore)')
  },
  name: {
    required: true,
    minLength: 2,
    message: t('Nome deve ter no mínimo 2 caracteres')
  },
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: t('Email inválido')
  },
  phone_number: {
    required: true,
    pattern: /^\+\d{8,15}$/,
    message: t('Telefone deve estar no formato internacional, ex.: +5511999999999')
  },
  documento: {
    required: true
  },
  password: {
    required: !props.editMode, // Not required in edit mode (handled by ChangePasswordModal)
    minLength: 8,
    message: t('Senha deve ter no mínimo 8 caracteres')
  },
  confirmPassword: {
    required: !props.editMode, // Not required in edit mode
    custom: () => localData.value.password === localData.value.confirmPassword,
    message: t('As senhas não coincidem')
  }
}));

// Validate single field
const validateField = (fieldName: string): boolean => {
  const rule = validationRules.value[fieldName];
  const value = localData.value[fieldName];

  if (rule.required && !value) {
    errors.value[fieldName] = t('Campo obrigatório');
    return false;
  }

  if (rule.minLength && value && value.length < rule.minLength) {
    errors.value[fieldName] = rule.message;
    return false;
  }

  if (rule.pattern && value && !rule.pattern.test(value)) {
    errors.value[fieldName] = rule.message;
    return false;
  }

  if (rule.custom && !rule.custom()) {
    errors.value[fieldName] = rule.message;
    return false;
  }

  delete errors.value[fieldName];
  return true;
};

// Validate all fields
const validateAll = (): boolean => {
  let isValid = true;

  Object.keys(validationRules.value).forEach(fieldName => {
    if (!validateField(fieldName)) {
      isValid = false;
    }
  });

  return isValid;
};

// Watch for changes and validate
watch(localData, () => {
  const isValid = validateAll();
  emit('validate', isValid, errors.value);
}, { deep: true });

// Initial validation
validateAll();
emit('validate', false, errors.value);
</script>

<style scoped>
.onboarding-step {
  padding: 20px 0;
}

.step-header {
  margin-bottom: 24px;
}

.step-header h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin: 0;
  color: #666;
  font-size: 0.875rem;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #333;
}

.form-field label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-field :deep(.p-password-input) {
  width: 100%;
}

.form-field small {
  font-size: 0.75rem;
}

.form-field small.p-help {
  color: #666;
}

.form-field small.p-error {
  color: #ef4444;
}

/* Responsive */
@media (max-width: 768px) {
  .form-grid {
    gap: 12px;
  }
}
</style>
